import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown, ArrowUp, ArrowUpDown, Archive, ChevronLeft, ChevronRight, MailX, Search, UserRound, X,
} from 'lucide-react';
import './TicketTableDashboard.css';
import './TicketTableArchive.css';
import { EmptyState, Segmented } from '../design/ui';
import {
  RESOLUTION_ACTOR_VALUES,
  TERMINAL_STATUS_VALUES,
  getResolutionActor,
  getResolutionActorLabel,
  getStatusLabel,
} from '../constants/ticketStatuses';
import {
  ChannelMark, MoodInline, TL_NS, clientIdentity, fmtFr, formatParisDateTime, formatParisDay, initialsOf, statusTone,
} from './TicketListsShared';

// Same geometry as the queue (TicketTableDashboard): each column's second
// line holds the fact that belongs to it — humeur and channel under the
// client, the e-mail's own subject under the subject, who resolved it and
// when under the status. Widths in px include the padding: status 196 holds
// « Résolu - accepté » and every category but the longest (« Réponse
// automatique / absence » wraps inside its badge), carrier 128 a carrier
// name, created 104 « 28 oct. 2025 ». The client takes 24 % of a table never
// narrower than 900 px (≥ 216 px), the subject the rest (≥ 256 px): no
// sideways scroll at 1 280 px nor 1 440 px (29/10).
const COLUMN_WIDTHS = { client: '24%', status: 196, carrier: 128, created: 104 };
const TABLE_MIN_WIDTH = 900;

// The archive's table: resolved / closed tickets, or — « Non-demandes » —
// the e-mails the AI closed as not a request (KI-282), with their category.
const TicketTableArchive = ({
  tickets,
  user, // FA accounts: company_name is a shop mapping, never the client
  onTicketClick,
  pagination,
  onPageChange,
  statusFilter,
  resolvedByFilter,
  nonRequestsFilter = false,
  onFilterChange,
  isLoading = false,
  kindCounts = null,
}) => {
  const { t } = useTranslation();
  const tl = (key, fallback, options) => t(`${TL_NS}.${key}`, fallback, options);
  const hasFaAccount = Boolean(user?.fa_account);
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortField, setSortField] = useState('created_at');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const statusOptions = TERMINAL_STATUS_VALUES;

  const sortTickets = (list, field, order) => {
    return [...list].sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      if (field === 'created_at') {
        return order === 'asc' ? new Date(valA) - new Date(valB) : new Date(valB) - new Date(valA);
      }
      if (field === 'sentiment_score') {
        return order === 'asc' ? (valA || 0) - (valB || 0) : (valB || 0) - (valA || 0);
      }
      return order === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
    });
  };

  const filterTickets = (list) => {
    return list.filter((ticket) => {
      const matchesStatus = statusFilter ? ticket.status === statusFilter : true;
      const matchesResolvedBy = resolvedByFilter
        ? getResolutionActor(ticket) === resolvedByFilter
        : true;
      return matchesStatus && matchesResolvedBy;
    });
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    setIsSearching(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/tickets/search/', {
        params: {
          query: searchQuery,
          sort_field: sortField,
          sort_order: sortOrder,
          terminal_only: true,
          status: statusFilter || undefined,
          resolved_by: resolvedByFilter || undefined,
          non_requests: nonRequestsFilter ? true : undefined,
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching tickets:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim() === '') {
      setSearchResults(null);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleSortChange = (field) => {
    setSortField(field);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    if (searchResults) {
      handleSearch();
    }
  };

  const handleStatusFilterChange = (e) => {
    onFilterChange({ status: e.target.value, resolvedBy: resolvedByFilter, nonRequests: nonRequestsFilter });
    setSearchResults(null);
  };

  const handleResolvedByFilterChange = (e) => {
    onFilterChange({ status: statusFilter, resolvedBy: e.target.value, nonRequests: nonRequestsFilter });
    setSearchResults(null);
  };

  // KI-282: the e-mails the AI closed as « not a request » are left out of the
  // archive; this filter lists them alone.
  const handleKindChange = (kind) => {
    onFilterChange({ status: statusFilter, resolvedBy: resolvedByFilter, nonRequests: kind === 'non_requests' });
    setSearchResults(null);
  };

  // Scores spread 30–70: ≤ 25 reads as a problem, > 60 as good (archive scale).
  const moodTone = (score) => {
    if (score <= 25) return 'danger';
    if (score <= 60) return undefined;
    return 'success';
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="tq__sort-icon" aria-hidden="true" />;
    return sortOrder === 'asc'
      ? <ArrowUp className="tq__sort-icon is-active" aria-hidden="true" />
      : <ArrowDown className="tq__sort-icon is-active" aria-hidden="true" />;
  };

  const displayedTickets = searchResults !== null ? searchResults : (tickets || []);
  const sortedAndFilteredTickets = sortTickets(filterTickets(displayedTickets), sortField, sortOrder);

  const handlePageChangeInternal = (direction) => {
    if (!onPageChange || !pagination) return;
    if (direction === 'next' && pagination.has_next) {
      onPageChange(pagination.current_page + 1);
    } else if (direction === 'prev' && pagination.has_previous) {
      onPageChange(Math.max(1, pagination.current_page - 1));
    }
  };

  const kindOptions = [
    { value: 'tickets', label: tl('archive.kindClosed', 'Tickets clos'), count: kindCounts?.tickets },
    { value: 'non_requests', label: t('ticketTableArchive.kindNonRequests', 'Non-demandes'), count: kindCounts?.nonRequests },
  ].map((option) => ({ ...option, count: Number.isFinite(option.count) ? fmtFr(option.count) : undefined }));

  const total = searchResults !== null
    ? searchResults.length
    : (Number.isFinite(pagination?.total_tickets) ? pagination.total_tickets : null);
  const showLoadingRows = isLoading && searchResults === null;
  const columns = [
    { key: 'client', width: COLUMN_WIDTHS.client },
    { key: 'subject' },
    { key: 'status', width: COLUMN_WIDTHS.status },
    { key: 'carrier', width: COLUMN_WIDTHS.carrier },
    { key: 'created', width: COLUMN_WIDTHS.created },
  ];

  return (
    <section className="u-card tq__card archive-table">
      <div className="u-card__header tq__head">
        <div className="u-card__heading">
          <h2 className="u-card__title">
            {nonRequestsFilter ? t('ticketTableArchive.kindNonRequests', 'Non-demandes') : tl('archive.kindClosed', 'Tickets clos')}
            {total !== null && <span className="u-badge">{fmtFr(total)}</span>}
          </h2>
          <p className="u-card__desc">
            {nonRequestsFilter
              ? tl('archive.nonRequestsDesc', 'E-mails que l’IA a clos comme « pas une demande » : newsletters, notifications, réponses automatiques… Ils ne sont pas comptés comme tickets.')
              : tl('archive.closedDesc', 'Tickets résolus ou clos, du plus récent au plus ancien.')}
          </p>
        </div>
      </div>

      <div className="u-toolbar tq__toolbar">
        <Segmented
          options={kindOptions}
          value={nonRequestsFilter ? 'non_requests' : 'tickets'}
          onChange={handleKindChange}
          label={t('ticketTableArchive.kindFilter', 'Type de ticket')}
        />
        <select
          id="status-select"
          className="u-select archive-table__select"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          aria-label={t('ticketTableArchive.status', 'Statut')}
        >
          <option value="">{tl('archive.allStatuses', 'Tous les statuts')}</option>
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {getStatusLabel(t, status)}
            </option>
          ))}
        </select>
        <select
          id="resolved-by-select"
          className="u-select archive-table__select"
          value={resolvedByFilter}
          onChange={handleResolvedByFilterChange}
          aria-label={t('ticketTableArchive.resolvedByFilter', 'Filtrer par auteur de la résolution')}
          title={t('ticketTableArchive.resolvedByFilter', 'Filtrer par auteur de la résolution')}
        >
          <option value="">{t('ticketTableArchive.resolvedByAnyone', "Résolu par n'importe qui")}</option>
          {RESOLUTION_ACTOR_VALUES.map((actor) => (
            <option key={actor} value={actor}>
              {tl('archive.resolvedByActor', 'Résolu par : {{actor}}', { actor: t(`resolutionActors.${actor}`, actor) })}
            </option>
          ))}
        </select>
        <span className="u-toolbar__spacer" />
        <div className="u-input-group u-input-group--lead tq__search">
          <Search className="u-input-group__icon" aria-hidden="true" />
          <input
            id="search-input"
            type="search"
            className="u-input"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            placeholder={tl('archive.searchPlaceholder', 'Rechercher dans les archives…')}
            aria-label={t('ticketTableArchive.search', 'Rechercher')}
            aria-busy={isSearching}
          />
          {searchQuery && (
            <button type="button" className="u-input-group__btn" onClick={clearSearch}
              title={t('clearSearch', 'Effacer la recherche')} aria-label={t('clearSearch', 'Effacer la recherche')}>
              <X />
            </button>
          )}
        </div>
      </div>

      <div className="u-table-scroll">
        <table className="u-table tq__table archive-table__table" style={{ minWidth: TABLE_MIN_WIDTH }}>
          <colgroup>
            {columns.map((column) => <col key={column.key} style={column.width ? { width: column.width } : undefined} />)}
          </colgroup>
          <thead>
            <tr>
              <th aria-sort={sortField === 'sentiment_score' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <span className="tq__th">
                  <span>{t('company', 'Client')}</span>
                  <span className="tq__th-sep" aria-hidden="true">·</span>
                  <button type="button" className="tq__sort" onClick={() => handleSortChange('sentiment_score')}
                    title={tl('archive.sortOnPage', 'Trie les tickets de cette page')}>
                    {t('sentimentScore', 'Humeur')} <SortIcon field="sentiment_score" />
                  </button>
                </span>
              </th>
              <th>{t('subject', 'Sujet')}</th>
              <th>
                <span className="tq__th">
                  <span>{t('status', 'Statut')}</span>
                  <span className="tq__th-sep" aria-hidden="true">·</span>
                  <span>{t('ticketTableArchive.resolvedBy', 'Résolu par')}</span>
                </span>
              </th>
              <th>{t('carrier', 'Transporteur')}</th>
              <th className="tq__num" aria-sort={sortField === 'created_at' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" className="tq__sort" onClick={() => handleSortChange('created_at')}
                  title={tl('archive.sortOnPage', 'Trie les tickets de cette page')}>
                  {t('createdAt', 'Créé le')} <SortIcon field="created_at" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {showLoadingRows && [0, 1, 2, 3, 4, 5].map((index) => (
              <tr key={`loading-${index}`} className="tq__loading-row" aria-hidden="true">
                <td colSpan={columns.length}><span className="u-skeleton tq__loading-bar" /></td>
              </tr>
            ))}
            {!showLoadingRows && sortedAndFilteredTickets.map((ticket) => {
              const statusLabel = getStatusLabel(t, ticket.status);
              const resolvedByLabel = getResolutionActorLabel(t, ticket);
              const resolvedAt = ticket.resolution?.at;
              const original = (ticket.metadata || {}).original_subject;
              const tone = statusTone(ticket.status);
              // The client, as the queue shows it: the company (never in FA
              // mode, never « Unknown »), else a name read from the e-mail.
              const { name: clientName, email } = clientIdentity(ticket, { hasFaAccount });
              const channelLabel = ticket.channel_name || t('unknown', 'Inconnu');
              const moodTitle = t('listsPolishUi.table.moodTitle', 'Humeur estimée par l’IA : {{score}}/100', {
                score: Math.round(Number(ticket.sentiment_score)),
              });
              const resolvedTitle = [
                resolvedByLabel && tl('archive.resolvedByActor', 'Résolu par : {{actor}}', { actor: resolvedByLabel }),
                resolvedAt && formatParisDateTime(resolvedAt),
              ].filter(Boolean).join(' · ');
              const statusBadge = ticket.non_request ? (
                <span
                  className="u-badge u-badge--info tq__status"
                  title={t('ticketTableArchive.nonRequestTitle', 'Clos par l’IA : pas une demande — {{reason}}', {
                    reason: ticket.non_request.reason || '',
                  })}
                >
                  <MailX aria-hidden="true" />
                  <span className="tq__status-label">
                    {t(`ticketTableArchive.nonRequestCategories.${ticket.non_request.category}`,
                      ticket.non_request.label || ticket.non_request.category)}
                  </span>
                </span>
              ) : (
                <span className={`u-badge${tone ? ` u-badge--${tone}` : ''} tq__status`} title={statusLabel}>
                  <span className="u-badge__dot" aria-hidden="true" />
                  <span className="tq__status-label">{statusLabel}</span>
                </span>
              );
              return (
                <tr key={ticket._id} className="tq__row" onClick={() => onTicketClick(ticket._id)}>
                  <td title={[clientName, email, channelLabel].filter(Boolean).join(' · ')}>
                    <div className="tq__person">
                      <span className="tq__avatar" aria-hidden="true">
                        {clientName ? initialsOf(clientName) : <UserRound strokeWidth={1.8} />}
                      </span>
                      <div className="tq__lines">
                        {clientName
                          ? <span className="tq__primary">{clientName}</span>
                          : <span className="tq__primary tq__muted">—</span>}
                        <span className="tq__secondary">
                          <MoodInline
                            score={ticket.sentiment_score}
                            toneOf={moodTone}
                            title={moodTitle}
                            emptyTitle={t('ticketTableArchive.notAvailable', 'N/D')}
                          />
                          <ChannelMark name={ticket.channel_name} />
                          {email || channelLabel}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="tq__lines">
                      <button
                        type="button"
                        className="tq__subject-link"
                        title={ticket.subject}
                        onClick={(event) => { event.stopPropagation(); onTicketClick(ticket._id); }}
                      >
                        {ticket.subject || tl('table.noSubject', '(sans objet)')}
                      </button>
                      {original && original !== ticket.subject && (
                        <span className="tq__secondary" title={`${t('originalSubject', "Objet d'origine")} : ${original}`}>{original}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="tq__status-cell" title={resolvedTitle || undefined}>
                      <span className="tq__status-line">{statusBadge}</span>
                      {(resolvedByLabel || resolvedAt) && (
                        <span className="tq__secondary tq__status-meta">
                          {resolvedByLabel && <span className="tq__who">{resolvedByLabel}</span>}
                          {resolvedByLabel && resolvedAt && <span className="tq__dot-sep" aria-hidden="true">·</span>}
                          {resolvedAt && <span className="tq__wait">{tl('archive.on', 'le {{date}}', { date: formatParisDay(resolvedAt) })}</span>}
                        </span>
                      )}
                    </span>
                  </td>
                  <td title={ticket.carrier_name || undefined}>
                    {ticket.carrier_name
                      ? <span className="tq__primary tq__primary--plain">{ticket.carrier_name}</span>
                      : <span className="tq__muted">—</span>}
                  </td>
                  <td className="tq__num" title={formatParisDateTime(ticket.created_at)}>{formatParisDay(ticket.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showLoadingRows && (
        <span className="tq__sr-only" role="status">{t('archiveP.loadingTitle', 'Chargement des archives...')}</span>
      )}

      {!showLoadingRows && sortedAndFilteredTickets.length === 0 && (
        <EmptyState
          icon={searchResults !== null ? Search : nonRequestsFilter ? MailX : Archive}
          title={searchResults !== null
            ? t('ticketTableArchive.noMatchingTickets', 'Aucun ticket correspondant trouvé.')
            : nonRequestsFilter
              ? tl('archive.noNonRequests', 'Aucune non-demande')
              : tl('archive.empty', 'Aucun ticket archivé')}
        >
          {searchResults === null && (nonRequestsFilter
            ? tl('archive.noNonRequestsHint', 'Les e-mails que l’IA classe comme « pas une demande » arriveront ici, avec leur catégorie.')
            : (statusFilter || resolvedByFilter)
              ? tl('archive.emptyFiltered', 'Aucun ticket clos ne correspond à ces filtres.')
              : tl('archive.emptyHint', 'Les tickets résolus quittent la file et sont conservés ici.'))}
        </EmptyState>
      )}

      <div className="u-pagination tq__footer">
        <span>
          {searchResults !== null
            ? t('searchResultsCount', '{{count}} résultats', { count: fmtFr(searchResults.length) })
            : (Number.isFinite(pagination?.total_tickets)
              ? t('paginationTickets', '{{count}} tickets', { count: fmtFr(pagination.total_tickets) })
              : null)}
        </span>
        {pagination && searchResults === null && (
          <div className="u-pagination__pages">
            <button type="button" className="u-btn u-btn--secondary u-btn--icon u-btn--sm" onClick={() => handlePageChangeInternal('prev')}
              disabled={!pagination.has_previous} aria-label={t('common.previousPage', 'Page précédente')} title={t('common.previousPage', 'Page précédente')}>
              <ChevronLeft strokeWidth={2} />
            </button>
            <span className="u-num">
              {t('paginationPageOf', 'Page {{page}} / {{pages}}', { page: pagination.current_page || 1, pages: pagination.total_pages || 1 })}
            </span>
            <button type="button" className="u-btn u-btn--secondary u-btn--icon u-btn--sm" onClick={() => handlePageChangeInternal('next')}
              disabled={!pagination.has_next} aria-label={t('common.nextPage', 'Page suivante')} title={t('common.nextPage', 'Page suivante')}>
              <ChevronRight strokeWidth={2} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default TicketTableArchive;
